import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Video, FileText, AlertTriangle, GraduationCap, ArrowRight, Play, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";
import { useReportTileEmpty } from "./DashboardTile";

interface TrainingModule {
  id: number;
  title: string;
  description: string;
  content: string;
  contentType: string;
  duration: number;
}

export default function TrainingModules() {
  const { data: modules, isLoading } = useQuery<TrainingModule[]>({
    queryKey: ["/api/training-modules/assigned/1"], // Eventually this would use the actual user ID
  });

  useReportTileEmpty("training_modules", !isLoading && (modules?.length ?? 0) === 0);

  const getModuleIcon = (contentType: string) => {
    switch (contentType) {
      case "video":
        return <Video className="h-5 w-5" />;
      case "pdf":
      case "document":
        return <FileText className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getModuleColor = (index: number) => {
    const colors = [
      { bg: "bg-gradient-to-br from-indigo-500 to-purple-600", light: "bg-indigo-50 dark:bg-indigo-950/30" },
      { bg: "bg-gradient-to-br from-rose-500 to-pink-600", light: "bg-rose-50 dark:bg-rose-950/30" },
      { bg: "bg-gradient-to-br from-cyan-500 to-blue-600", light: "bg-cyan-50 dark:bg-cyan-950/30" },
    ];
    return colors[index % colors.length];
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m` 
        : `${hours}h`;
    }
  };

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-4 border-b border-border flex flex-row items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 shadow-md">
            <GraduationCap className="h-5 w-5 text-white" />
          </div>
          <div>
            <SectionHelp contentId="dashboard:training" tooltip="Learn about assigned training">
              <CardTitle className="text-lg font-bold">Assigned Training</CardTitle>
            </SectionHelp>
            <p className="text-sm text-muted-foreground">Continue your learning journey</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 gap-1 font-semibold"
        >
          View All
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="p-6">
        {isLoading ? (
          <div className="space-y-4">
            <TrainingModuleSkeleton />
            <TrainingModuleSkeleton />
          </div>
        ) : modules && modules.length > 0 ? (
          <div className="space-y-4">
            {modules.slice(0, 2).map((module, index) => {
              const colorScheme = getModuleColor(index);
              return (
                <div 
                  key={module.id} 
                  className={cn(
                    "group relative flex items-start gap-4 p-5 rounded-2xl cursor-pointer",
                    "bg-card border border-border",
                    "hover:shadow-lg hover:border-border transition-all duration-300"
                  )}
                >
                  <div className={cn(
                    "flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-white shadow-lg",
                    colorScheme.bg,
                    "group-hover:scale-105 transition-transform duration-300"
                  )}>
                    {getModuleIcon(module.contentType)}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h3 className="font-semibold text-foreground group-hover:text-indigo-600 transition-colors">
                      {module.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{module.description}</p>
                    <div className="mt-3 flex items-center gap-4 text-xs">
                      <span className="flex items-center gap-1 text-muted-foreground font-medium bg-muted px-2 py-1 rounded-md">
                        <Clock className="h-3 w-3" />
                        {formatDuration(module.duration)}
                      </span>
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        Due in 5 days
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <Button 
                      size="sm" 
                      className={cn(
                        "gap-2 shadow-md",
                        "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                      )}
                    >
                      <Play className="h-3 w-3" />
                      Start
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium">No assigned training</p>
            <p className="text-sm text-muted-foreground mt-1">You're all caught up!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TrainingModuleSkeleton() {
  return (
    <div className="flex items-start gap-4 p-5 border border-border rounded-2xl">
      <Skeleton className="w-14 h-14 rounded-xl" />
      <div className="flex-grow">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-full mb-1" />
        <Skeleton className="h-4 w-2/3 mb-3" />
        <div className="flex gap-3">
          <Skeleton className="h-6 w-16 rounded-md" />
          <Skeleton className="h-6 w-20" />
        </div>
      </div>
      <Skeleton className="h-9 w-20 rounded-md" />
    </div>
  );
}
